'use client'

import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Fragment } from 'react'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context'
import { configPublic } from '@src/config.public'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'

interface MenuItemProps {
  icon: React.ReactNode
  label: string
  href?: string
}

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, href }) => {
  const trackSelectNavCta = useTrackSelectNavCta()
  return (
    <div className="flex items-center rounded-md">
      <Link
        href={href ?? ''}
        rel="noopener"
        target="_blank"
        className="flex items-center rounded-md px-4 py-1 hover:font-bold hover:underline"
        onClick={() => {
          trackSelectNavCta({ 'cta url': href })
        }}
      >
        <div className="mr-3 flex size-6 items-center justify-center">{icon}</div>
        <span className="text-body-sm">{label}</span>
      </Link>
    </div>
  )
}

export const TopNavV5AccountPanelContent = () => {
  const { setAccountExpanded, account } = useTopNavV5()
  const { user, getLoginHref } = useSession()
  const loginHref = getLoginHref()
  const trackSelectNavCta = useTrackSelectNavCta()
  const pathname = usePathname()
  const { t, locale } = useLocale('common')

  const logOutHref = `/logout?redirectUrl=${pathname}`

  const welcomeText =
    user?.firstName && user?.firstName?.toLowerCase() !== 'guest'
      ? `${account?.welcomeCopy}, ${user?.firstName} ${user?.lastName}.`
      : `${account?.welcomeCopy}!`

  const isLoggedIn = Boolean(user?.email)

  const renderLinks = () => {
    return (
      <div className="flex flex-col">
        {account?.links?.map((link, i) => (
          <LocalizedLink
            key={i}
            href={link?.href ?? ''}
            rel={link?.newTab ? 'noopener noreferrer' : ''}
            target={link?.newTab ? '_blank' : '_self'}
            className="group my-1 flex items-center justify-between py-1 text-body-base hover:underline"
            onClick={() => trackSelectNavCta({ 'cta url': link?.href })}
          >
            <div className=" flex items-center font-bold">{link?.title}</div>
            <ChevronRightIcon className="size-5 text-bodi-gray-medium group-hover:text-bodi-green-bright-500" />
          </LocalizedLink>
        ))}
      </div>
    )
  }

  const renderLoggedInContent = () => {
    return (
      <div className="flex flex-col pt-9 lg:pt-5">
        <div className="text-heading-display-3 mb-1">{account?.title}</div>
        <div className="mb-8 text-body-base text-bodi-gray-butters">{welcomeText}</div>
        <div className="mb-5 flex flex-col gap-1 overflow-hidden rounded-[30px] border border-bodi-gray-dark pb-2 transition-colors">
          {account?.loggedInLinks?.map((link, i) => {
            const createBODurl = (path: string) => {
              return `${configPublic.general.bodUrl}${path}?code=BODI_ACCOUNT_NAV&locale=${locale.code.replace('-', '_')}`
            }
            return (
              <Fragment key={i}>
                <LocalizedLink
                  href={link?.href ?? ''}
                  rel={link?.newTab ? 'noopener noreferrer' : ''}
                  target={link?.newTab ? '_blank' : '_self'}
                  className="flex items-center justify-between gap-2 bg-bodi-white/5 p-4  hover:bg-bodi-white/10"
                  onClick={() => trackSelectNavCta({ 'cta url': link?.href })}
                >
                  <div className="flex items-center gap-2">
                    <Image
                      className="shrink-0 rounded-xl bg-bodi-gray-dark object-cover"
                      width={48}
                      height={48}
                      src={link?.imageUrl ?? ''}
                      alt={link?.imageAlt ?? ''}
                    />

                    <div className="flex flex-col">
                      <div className="text-heading-5">{link?.title}</div>
                      <div className="text-body-sm text-bodi-gray-butters">{link?.subtitle}</div>
                    </div>
                  </div>
                  <ChevronRightIcon className="size-6 shrink-0 stroke-[2px]" />
                </LocalizedLink>
                <MenuItem
                  icon={
                    <Image
                      src="/images/squares_four.svg"
                      alt="squares_four"
                      width={16}
                      height={16}
                    />
                  }
                  label={t('my-dashboard')}
                  href={createBODurl('/dashboard')}
                />
                <MenuItem
                  icon={
                    <Image src="/images/play_circle.svg" alt="play_circle" width={16} height={16} />
                  }
                  label={t('digital-purchases')}
                  href={createBODurl('/digital-purchases')}
                />
                <MenuItem
                  icon={<Image src="/images/heart.svg" alt="heart" width={16} height={16} />}
                  label={t('favorites')}
                  href={createBODurl('/favorites')}
                />
                <MenuItem
                  icon={<Image src="/images/trophy.svg" alt="trophy" width={16} height={16} />}
                  label={t('badges')}
                  href={createBODurl('/dashboard/badges')}
                />
                <MenuItem
                  icon={
                    <Image src="/images/note_pencil.svg" alt="note_pencil" width={16} height={16} />
                  }
                  label={t('tracking')}
                  href={createBODurl('/tracking')}
                />
                <MenuItem
                  icon={<Image src="/images/trend_up.svg" alt="trend_up" width={16} height={16} />}
                  label={t('my-progress')}
                  href={createBODurl('/tracking/my-progress')}
                />
              </Fragment>
            )
          })}
        </div>
        {renderLinks()}
        <a
          href={logOutHref}
          className="mt-8 self-center font-medium text-bodi-green-bright-500 underline"
        >
          {account?.signOutCopy}
        </a>
      </div>
    )
  }

  const renderLoggedOutContent = () => {
    return (
      <div className="pt-9 lg:pt-4">
        <div className="mb-8">
          <div className="text-heading-display-3">{account?.loggedOutTitle}</div>
          <div className="pt-1 text-body-base text-bodi-gray-butters">
            {account?.loggedOutSubtitle}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <a
            onClick={() => {
              trackSelectNavCta({ 'cta url': loginHref })
            }}
            data-testid="logInBtn"
            href={loginHref}
            className="flex cursor-pointer gap-2 hover:text-bodi-white"
          >
            <Button
              size="medium"
              variant="black"
              className="w-full border border-solid border-bodi-gray-medium"
            >
              {account?.logInCopy}
            </Button>
          </a>
          <LocalizedLink
            href="/offers"
            onClick={() => {
              trackSelectNavCta({ 'cta url': '/offers' })
            }}
            data-testid="offersLink"
            className="flex cursor-pointer gap-2 hover:text-bodi-white"
          >
            <Button size="medium" variant="primary" className="w-full">
              {account?.bodiCta?.title}
            </Button>
          </LocalizedLink>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 p-4 text-white lg:p-8">
      <button
        className="absolute top-4 right-4 rounded-md p-1 hover:bg-bodi-gray-dark"
        onClick={() => setAccountExpanded(false)}
      >
        <XMarkIcon className="size-6 stroke-[2px]" />
      </button>
      {isLoggedIn ? renderLoggedInContent() : renderLoggedOutContent()}
    </div>
  )
}
